import { Injectable, inject } from '@angular/core';
import { IReporteBase, IUbicacion } from 'modelos/src';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import {
  IVehiculoPaso,
  IZonaPaso,
  MapInstanceFilter,
} from '../../../interfaces';
import { DialogService } from '../../../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../../../auxiliares/servicios/helper.service';
import { DateRange } from '../../../../../../../auxiliares/date-range-selector/date-range-selector.component';

@Injectable()
export class BusquedaZonaService {
  private dialogService = inject(DialogService);
  private helper = inject(HelperService);

  // Algoritmo de ray-casting para verificar si un punto está dentro de un polígono
  puntoEnPoligono(
    punto: [number, number],
    poligono: [number, number][],
  ): boolean {
    const x = punto[0];
    const y = punto[1];
    let dentro = false;

    for (let i = 0, j = poligono.length - 1; i < poligono.length; j = i++) {
      const xi = poligono[i][0];
      const yi = poligono[i][1];
      const xj = poligono[j][0];
      const yj = poligono[j][1];

      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        dentro = !dentro;
      }
    }

    return dentro;
  }

  // Verifica si un punto está dentro de un círculo usando la fórmula de Haversine
  puntoEnCirculo(
    punto: [number, number],
    centro: [number, number],
    radioMetros: number,
  ): boolean {
    const R = 6371000; // Radio de la Tierra en metros
    const lat1 = (centro[1] * Math.PI) / 180;
    const lat2 = (punto[1] * Math.PI) / 180;
    const deltaLat = ((punto[1] - centro[1]) * Math.PI) / 180;
    const deltaLng = ((punto[0] - centro[0]) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distancia = R * c;

    return distancia <= radioMetros;
  }

  // Verifica si un punto está dentro de una zona (ubicación)
  verificarPuntoEnZona(
    punto: [number, number],
    ubicacion: IUbicacion,
  ): boolean {
    if (!ubicacion.geojson) return false;

    if (ubicacion.geojson.type === 'Polygon') {
      return this.puntoEnPoligono(punto, ubicacion.geojson.coordinates[0]);
    } else if (ubicacion.geojson.type === 'Point') {
      const radio = 100; // metros por defecto
      return this.puntoEnCirculo(
        punto,
        ubicacion.geojson.coordinates as [number, number],
        radio,
      );
    }

    return false;
  }

  // Procesa los reportes del vehículo para determinar qué zonas visitó
  procesarReportesParaZonas(
    reportes: IReporteBase<'Tracker 4G'>[],
    ubicaciones: IUbicacion[],
    minutosMinimos: number | null,
  ): IZonaPaso[] {
    const zonasPasos: IZonaPaso[] = [];
    const ubicacionesConGeojson = ubicaciones.filter(
      (u) =>
        u.geojson &&
        (u.geojson.type === 'Polygon' || u.geojson.type === 'Point'),
    );

    if (ubicacionesConGeojson.length === 0 || reportes.length === 0) {
      return [];
    }

    for (const ubicacion of ubicacionesConGeojson) {
      let entro: string | null = null;
      let salio: string | null = null;
      let estaDentro = false;

      for (const reporte of reportes) {
        const punto =
          reporte.valores?.geojson || reporte.valores?.snappedGeojson;
        if (!punto || !punto.coordinates) continue;

        const dentroDeZona = this.verificarPuntoEnZona(
          punto.coordinates as [number, number],
          ubicacion,
        );

        if (dentroDeZona && !estaDentro) {
          estaDentro = true;
          entro = reporte.valores.timestamp;
          salio = reporte.valores.timestamp;
        } else if (dentroDeZona && estaDentro) {
          salio = reporte.valores.timestamp;
        } else if (!dentroDeZona && estaDentro) {
          estaDentro = false;
          if (entro && salio) {
            const duracionMs =
              new Date(salio).getTime() - new Date(entro).getTime();
            const duracionMinutos = Math.round(duracionMs / (1000 * 60));

            if (!minutosMinimos || duracionMinutos >= minutosMinimos) {
              zonasPasos.push({
                idUbicacion: ubicacion._id!,
                ubicacion: ubicacion,
                entro: entro,
                salio: salio,
                duracionMinutos: duracionMinutos,
              });
            }
          }
          entro = null;
          salio = null;
        }
      }

      // Si terminó dentro de la zona
      if (estaDentro && entro && salio) {
        const duracionMs =
          new Date(salio).getTime() - new Date(entro).getTime();
        const duracionMinutos = Math.round(duracionMs / (1000 * 60));

        if (!minutosMinimos || duracionMinutos >= minutosMinimos) {
          zonasPasos.push({
            idUbicacion: ubicacion._id!,
            ubicacion: ubicacion,
            entro: entro,
            salio: salio,
            duracionMinutos: duracionMinutos,
          });
        }
      }
    }

    // Ordenar por fecha de entrada (más reciente primero)
    zonasPasos.sort(
      (a, b) => new Date(b.entro).getTime() - new Date(a.entro).getTime(),
    );

    return zonasPasos;
  }

  // Filtra interacciones de búsqueda según los filtros activos
  filtrarInteracciones(
    interacciones: IVehiculoPaso[],
    filtros: MapInstanceFilter | undefined,
  ): IVehiculoPaso[] {
    if (!interacciones || interacciones.length === 0) {
      return [];
    }

    if (!filtros) {
      return interacciones;
    }

    let resultado = [...interacciones];

    // Filtro por clientes
    if (
      filtros.idsClientesVehiculos &&
      filtros.idsClientesVehiculos.length > 0
    ) {
      resultado = resultado.filter(
        (i) =>
          i.vehiculo?.idCliente &&
          filtros.idsClientesVehiculos!.includes(i.vehiculo.idCliente),
      );
    }

    // Filtro por grupos
    if (filtros.gruposVehiculos && filtros.gruposVehiculos.length > 0) {
      resultado = resultado.filter(
        (i) =>
          i.vehiculo?.idGrupo &&
          filtros.gruposVehiculos!.includes(i.vehiculo.idGrupo),
      );
    }

    // Filtro por funciones
    if (filtros.funcionVehiculos && filtros.funcionVehiculos.length > 0) {
      resultado = resultado.filter(
        (i) =>
          i.vehiculo?.funcion &&
          filtros.funcionVehiculos!.includes(i.vehiculo.funcion),
      );
    }

    // Filtro por vehículos específicos
    if (
      filtros.idsVehiculosFiltrados &&
      filtros.idsVehiculosFiltrados.length > 0
    ) {
      resultado = resultado.filter(
        (i) =>
          i.idVehiculo && filtros.idsVehiculosFiltrados!.includes(i.idVehiculo),
      );
    } else if (filtros.idsVehiculos && filtros.idsVehiculos.length > 0) {
      resultado = resultado.filter(
        (i) => i.idVehiculo && filtros.idsVehiculos!.includes(i.idVehiculo),
      );
    }

    return resultado;
  }

  generarVehiculosPorZonaBlobFromData(
    interacciones: IVehiculoPaso[],
    nombreZona: string,
    clientesMap?: Map<string, string>,
  ): Blob {
    const fmtDate = (d: string) => {
      const dt = new Date(d);
      dt.setHours(dt.getHours() - 3);
      return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }).format(dt);
    };
    const resultado = interacciones.map((item) => ({
      ['Zona']: nombreZona,
      ['Vehículo']: item.vehiculo?.identificacion || item.idVehiculo,
      ...(clientesMap ? { ['Cliente']: clientesMap.get(item.vehiculo?.idCliente?.toString()) || item.vehiculo?.idCliente || '' } : {}),
      ['Entró']: fmtDate(item.entro),
      ['Salió']: fmtDate(item.salio),
      ['Duración (min)']: Math.round(
        (new Date(item.salio).getTime() - new Date(item.entro).getTime()) / (1000 * 60),
      ),
    }));
    const worksheet = XLSX.utils.json_to_sheet(resultado);
    const workbook: XLSX.WorkBook = { Sheets: { Vehiculos: worksheet }, SheetNames: ['Vehiculos'] };
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  generarZonasPorVehiculoBlobFromData(zonasPasos: IZonaPaso[], clientesMap?: Map<string, string>): Blob {
    const fmtDate = (d: string) => {
      const dt = new Date(d);
      dt.setHours(dt.getHours() - 3);
      return new Intl.DateTimeFormat('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' }).format(dt);
    };
    const sorted = [...zonasPasos].sort((a, b) => {
      const zonaA = (a.ubicacion?.identificacion || 'Sin nombre').localeCompare(b.ubicacion?.identificacion || 'Sin nombre', 'es');
      if (zonaA !== 0) return zonaA;
      return new Date(a.entro).getTime() - new Date(b.entro).getTime();
    });
    const resultado = sorted.map((item) => ({
      ['Zona']: item.ubicacion?.identificacion || 'Sin nombre',
      ...(clientesMap ? { ['Cliente']: clientesMap.get(item.ubicacion?.idCliente?.toString()) || item.ubicacion?.idCliente || '' } : {}),
      ['Dirección']: item.ubicacion?.direccion || '',
      ['Entró']: fmtDate(item.entro),
      ['Salió']: item.salio ? fmtDate(item.salio) : '',
      ['Duración (min)']: item.duracionMinutos || 0,
    }));
    const worksheet = XLSX.utils.json_to_sheet(resultado);
    const workbook: XLSX.WorkBook = { Sheets: { Zonas: worksheet }, SheetNames: ['Zonas'] };
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  // Exporta los vehículos encontrados en la zona a un archivo Excel
  async exportarVehiculos(
    interacciones: IVehiculoPaso[],
    // Qué acotó la búsqueda: una dirección, un círculo dibujado o una zona.
    // Antes era sólo la dirección y quedaba vacío en los otros dos casos.
    origen: string,
    radio: number | null,
    minutosMinimos: number,
    range: DateRange,
  ): Promise<void> {
    const res = await this.dialogService.confirm(
      'Exportar clientes',
      `¿Querés exportar la información de todos los vehículos en la zona?`,
    );
    if (!res) return;
    try {
      this.helper.notifSuccess('Documento exportado');
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    const resultado = interacciones.map((item) => {
      const fechaEntro = new Date(item.entro);
      const fechaSalio = new Date(item.salio);
      fechaEntro.setHours(fechaEntro.getHours() - 3);
      fechaSalio.setHours(fechaSalio.getHours() - 3);
      return {
        ['Origen']: origen,
        ['Radio (mts)']: radio ?? '',
        ['Vehículo']: item.vehiculo.identificacion,
        ['Entró']: new Intl.DateTimeFormat('es-AR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false,
          timeZone: 'UTC',
        }).format(fechaEntro),
        ['Salió']: new Intl.DateTimeFormat('es-AR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false,
          timeZone: 'UTC',
        }).format(fechaSalio),
        ['Duración (min)']: Math.round(
          (new Date(item.salio).getTime() - new Date(item.entro).getTime()) /
            (1000 * 60),
        ),
      };
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(resultado);
    const workbook: XLSX.WorkBook = {
      Sheets: { Vehiculos: worksheet },
      SheetNames: ['Vehiculos'],
    };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(
      blob,
      `vehiculos-${minutosMinimos}-${range.start}-${range.end}${
        radio ? `-${radio}` : ''
      }.xlsx`,
    );
  }

  // Exporta las zonas visitadas por un vehículo a un archivo Excel
  async exportarZonas(
    zonasPasos: IZonaPaso[],
    vehiculoId: string,
    range: DateRange,
  ): Promise<void> {
    const res = await this.dialogService.confirm(
      'Exportar zonas',
      `¿Querés exportar la información de las zonas visitadas?`,
    );
    if (!res) return;

    try {
      this.helper.notifSuccess('Documento exportado');
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }

    const resultado = zonasPasos.map((item) => {
      const fechaEntro = new Date(item.entro);
      const fechaSalio = item.salio ? new Date(item.salio) : fechaEntro;
      fechaEntro.setHours(fechaEntro.getHours() - 3);
      fechaSalio.setHours(fechaSalio.getHours() - 3);
      return {
        ['Zona']: item.ubicacion?.identificacion || 'Sin nombre',
        ['Dirección']: item.ubicacion?.direccion || '',
        ['Entró']: new Intl.DateTimeFormat('es-AR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false,
          timeZone: 'UTC',
        }).format(fechaEntro),
        ['Salió']: new Intl.DateTimeFormat('es-AR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false,
          timeZone: 'UTC',
        }).format(fechaSalio),
        ['Duración (min)']: item.duracionMinutos || 0,
      };
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(resultado);
    const workbook: XLSX.WorkBook = {
      Sheets: { Zonas: worksheet },
      SheetNames: ['Zonas'],
    };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(
      blob,
      `zonas-vehiculo-${vehiculoId}-${range.start}-${range.end}.xlsx`,
    );
  }
}
